import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { marketplaceApi } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import LogoMark from "./LogoMark";

function initialsFor(user) {
  const label = user?.display_name || user?.full_name || user?.email || "TC";
  return label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "TC";
}

function sellerLabel(seller) {
  return seller?.store_name || seller?.business_name || seller?.user_name || "Verified seller";
}

function serviceImage(service) {
  return service?.image_src || service?.service_image_src || service?.image_url || "/images/tazy-transport-truck.jpg";
}

function DynamicMegaMenu({ type, data, closeMenu }) {
  const categories = useMemo(() => (data.categories || []).filter((category) => !category.parent && !category.parent_name), [data.categories]);
  const sellers = data.sellers || [];
  const transporterServices = data.transporter_services || [];
  const products = data.products || [];

  if (!type) return null;

  if (type === "categories") {
    return (
      <div className="market-mega market-mega--categories">
        <div className="market-mega__title">
          <div><span>Browse marketplace</span><h3>All categories</h3></div>
          <Link to="/search?tab=Products" onClick={closeMenu}>View all product listings</Link>
        </div>
        <div className="mega-category-layout">
          <div className="mega-category-list">
            {categories.slice(0, 14).map((category) => (
              <Link key={category.code || category.id} to={`/categories/${category.code || category.id}`} onClick={closeMenu}>
                <span>{category.name}</span><b>›</b>
              </Link>
            ))}
          </div>
          <div className="mega-preview-panel">
            <span>Popular live listings</span>
            <div className="mega-round-grid mega-round-grid--cards">
              {products.slice(0, 8).map((product) => (
                <Link key={product.id} to={`/products/${product.slug}`} onClick={closeMenu}>
                  <img src={product.image_src || product.image_url || "/images/tazy-source-goods.jpg"} alt={product.title} />
                  <small>{product.title}</small>
                </Link>
              ))}
              {!products.length ? <p>No public products are live yet.</p> : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "sellers") {
    return (
      <div className="market-mega market-mega--sellers">
        <div className="market-mega__title">
          <div><span>Supplier directory</span><h3>Verified sellers</h3></div>
          <Link to="/sellers" onClick={closeMenu}>Open seller directory</Link>
        </div>
        <div className="mega-seller-grid">
          {sellers.slice(0, 6).map((seller) => (
            <Link key={seller.id} to={`/search?q=${encodeURIComponent(sellerLabel(seller))}&tab=Suppliers`} onClick={closeMenu} className="mega-seller-card">
              <div className="mega-avatar">{seller.logo_src ? <img src={seller.logo_src} alt={sellerLabel(seller)} /> : <span>{sellerLabel(seller).slice(0, 2).toUpperCase()}</span>}</div>
              <div><strong>{sellerLabel(seller)}</strong><small>{seller.city || seller.province || "Zimbabwe"} • Verified storefront</small></div>
            </Link>
          ))}
          {!sellers.length ? <p>No verified sellers are public yet.</p> : null}
        </div>
        <div className="mega-footer-actions">
          <Link to="/sellers" onClick={closeMenu}>Browse all verified sellers</Link>
          <Link to="/register?type=SELLER" onClick={closeMenu}>Become a seller</Link>
        </div>
      </div>
    );
  }

  if (type === "transporters") {
    return (
      <div className="market-mega market-mega--transporters">
        <div className="market-mega__title">
          <div><span>Logistics marketplace</span><h3>Transporter services</h3></div>
          <Link to="/transporters" onClick={closeMenu}>Open transporter marketplace</Link>
        </div>
        <div className="mega-transport-grid">
          {transporterServices.slice(0, 4).map((service) => (
            <Link key={service.id} to="/transporters" onClick={closeMenu} className="mega-transport-card">
              <img src={serviceImage(service)} alt={service.service_name || "Transporter service"} />
              <strong>{service.service_name || "Delivery service"}</strong>
              <small>{service.courier_name || "Registered transporter"}</small>
            </Link>
          ))}
          {!transporterServices.length ? <p>No approved transporter services are public yet.</p> : null}
        </div>
        <div className="mega-footer-actions">
          <Link to="/transporters" onClick={closeMenu}>Browse transport services</Link>
          <Link to="/register?type=COURIER" onClick={closeMenu}>List courier service</Link>
        </div>
      </div>
    );
  }

  if (type === "business") {
    const cards = [
      { title: "Start selling", body: "Create a seller account and open your storefront.", to: "/register?type=SELLER" },
      { title: "Business account", body: "For supermarkets and larger shops with inventory operations.", to: "/register?type=BUSINESS" },
      { title: "Courier services", body: "Register transporter routes and delivery services.", to: "/register?type=COURIER" },
      { title: "Source what you need", body: "Send a buying request to suitable sellers.", to: "/request" },
    ];
    return (
      <div className="market-mega market-mega--business">
        <div className="market-mega__title">
          <div><span>Business solutions</span><h3>Operate on TAZY CART</h3></div>
          <Link to="/business-solutions" onClick={closeMenu}>View business solutions</Link>
        </div>
        <div className="mega-business-grid">
          {cards.map((card) => (
            <Link key={card.title} to={card.to} onClick={closeMenu}>
              <strong>{card.title}</strong>
              <small>{card.body}</small>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

export default function MarketplaceHeader({ active = "home" }) {
  const { isAuthenticated, user, logout } = useAuth();
  const [megaOpen, setMegaOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaData, setMegaData] = useState({ categories: [], sellers: [], transporter_services: [], products: [] });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    marketplaceApi.home()
      .then((res) => {
        if (!mounted) return;
        setMegaData(res.data || {});
      })
      .catch(() => {
        if (!mounted) return;
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(null);
  }, [location.pathname, location.search]);

  function signOut() {
    setMobileOpen(false);
    logout();
    navigate("/");
  }

  function closeMenu() {
    setMegaOpen(null);
  }

  function openMenu(type) {
    setMegaOpen((current) => (current === type ? null : type));
  }

  return (
    <header className={`market-header ${mobileOpen ? "mobile-nav-open" : ""}`} onMouseLeave={closeMenu}>
      <div className="market-header__top container">
        <LogoMark />
        <button className="market-mobile-menu-btn" type="button" onClick={() => setMobileOpen(true)} aria-label="Open marketplace menu" aria-expanded={mobileOpen}>
          <span aria-hidden="true">☰</span><strong>Menu</strong>
        </button>
        <div className="market-mobile-panel">
          <div className="market-mobile-panel__head">
            <strong>TAZY CART</strong>
            <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close marketplace menu">×</button>
          </div>
          <nav className="market-nav dynamic-market-nav">
            <button className="market-desktop-categories-btn" type="button" onClick={() => openMenu("categories")} onMouseEnter={() => setMegaOpen("categories")} onFocus={() => setMegaOpen("categories")}>All categories</button>
            <Link className="market-mobile-categories-link" to="/search?tab=Products" onClick={() => setMobileOpen(false)}>All categories</Link>
            <Link to="/sellers" onClick={() => setMobileOpen(false)} onMouseEnter={() => setMegaOpen("sellers")} onFocus={() => setMegaOpen("sellers")}>Verified sellers</Link>
            <Link to="/transporters" onClick={() => setMobileOpen(false)} onMouseEnter={() => setMegaOpen("transporters")} onFocus={() => setMegaOpen("transporters")}>Transporters</Link>
            <Link to="/business-solutions" onClick={() => setMobileOpen(false)} onMouseEnter={() => setMegaOpen("business")} onFocus={() => setMegaOpen("business")}>Business solutions</Link>
          </nav>
          <div className="market-actions market-actions--aware">
          {isAuthenticated ? (
            <>
              <Link className="nav-link dashboard-link" to="/portal" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <div className="market-user-mini" title={user?.email || "Signed in"}>
                {user?.avatar_src || user?.profile_picture_src ? <img src={user.avatar_src || user.profile_picture_src} alt="Profile" /> : <span>{initialsFor(user)}</span>}
                <small>{user?.display_name || user?.full_name || user?.email || "Account"}</small>
              </div>
              <button type="button" className="market-signout" onClick={signOut}>Sign out</button>
            </>
          ) : (
            <>
              <Link className={active === "login" ? "nav-link active" : "nav-link"} to="/login" onClick={() => setMobileOpen(false)}>Sign in</Link>
              <Link className="create-account" to="/register" onClick={() => setMobileOpen(false)}>Create account</Link>
            </>
          )}
          </div>
        </div>
      </div>
      <button className="market-mobile-backdrop" type="button" onClick={() => setMobileOpen(false)} aria-label="Close marketplace menu" tabIndex={mobileOpen ? 0 : -1} />
      <div className="container market-mega-shell" onMouseEnter={() => megaOpen && setMegaOpen(megaOpen)}>
        <DynamicMegaMenu type={megaOpen} data={megaData} closeMenu={closeMenu} />
      </div>
    </header>
  );
}
